/**
 * Pulling the reason out of a backend error response — the one place
 * every screen turns a failed request into words (#652).
 *
 * Kept identical in front-porch and member-portal — edit both, or neither.
 *
 * The API does not answer with one error shape. A validator rejection is
 * keyed by field, a service rejection is `{error, message}`, and the
 * member-portal endpoints nest under `{error: {code, message}}`. A screen
 * that only understands one of them falls back to "an error occurred",
 * which is what left somebody retrying a password the server had already
 * explained the problem with.
 *
 * **Never show `err.message` from a request.** The client is axios, and an
 * axios error's `message` is "Request failed with status code 500" or
 * "Network Error" — text for a developer, not for the person at the
 * screen. Forty-odd screens did exactly that before #652.
 */

/** Shown when the request never got an answer at all. */
export const NETWORK_ERROR_MESSAGE =
  "We couldn't reach the server. Check your connection and try again.";

/** The shapes the backend actually returns, in the order we try them. */
const readMessage = (data: unknown): string | null => {
  // A plain-text body — billing handlers answer this way, a 409 saying a
  // fine is already settled being typical.
  if (typeof data === 'string') return data.trim() ? data : null;
  if (!data || typeof data !== 'object') return null;
  const body = data as Record<string, unknown>;

  // `{ "error": { "code": "...", "message": "..." } }` — member portal.
  const nested = body.error;
  if (nested && typeof nested === 'object') {
    const inner = (nested as Record<string, unknown>).message;
    if (typeof inner === 'string' && inner) return inner;
  }

  // `{ "error": "Validation failed", "message": "..." }` — most services.
  if (typeof body.message === 'string' && body.message) {
    return body.message;
  }

  // `{ "error": "the whole explanation" }` — no `message` alongside it.
  // Checked *after* `message`, because where both are present `error` is
  // the category ("Validation failed") and `message` is the detail.
  //
  // This is the most common refusal shape in the API — 232 occurrences —
  // and it was the one shape this helper did not read, so every one of
  // them surfaced as the caller's fallback text. Found 2026-09-12 while
  // wiring the resend-invitation button, whose 400 says "This member has
  // already set up their account" and was showing "please try again".
  if (typeof nested === 'string' && nested) {
    return nested;
  }

  // `{ "detail": "..." }` — a handful of billing handlers.
  if (typeof body.detail === 'string' && body.detail) {
    return body.detail;
  }

  // `{ "password": [{ "code": "weak_password", "message": "..." }] }` —
  // the `validator` crate's per-field shape, used by anything deriving
  // `Validate`. Collect every field's messages so a form with two bad
  // fields does not report only the first.
  const fieldMessages: string[] = [];
  for (const value of Object.values(body)) {
    if (!Array.isArray(value)) continue;
    for (const entry of value) {
      if (entry && typeof entry === 'object') {
        const m = (entry as Record<string, unknown>).message;
        if (typeof m === 'string' && m) fieldMessages.push(m);
      }
    }
  }
  if (fieldMessages.length) return fieldMessages.join(' ');

  return null;
};

interface RequestError {
  response?: { status?: number; data?: unknown };
  request?: unknown;
  code?: string;
}

/**
 * True when the request went out and nothing came back — offline, DNS,
 * CORS, the API down. Axios marks that as a `request` with no `response`.
 */
export const isNetworkError = (err: unknown): boolean => {
  const e = err as RequestError | undefined;
  if (!e || typeof e !== 'object' || e.response) return false;
  return e.code === 'ERR_NETWORK' || e.request !== undefined;
};

/**
 * The backend's explanation, or `fallback` when it did not give one.
 *
 * Use this for every failed request. The fallback says what did not
 * happen, in the house style: "Couldn't load reservations." /
 * "Couldn't save the member." — see "Errors" in
 * `.claude/coding-standards.md`.
 */
export const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (isNetworkError(err)) return NETWORK_ERROR_MESSAGE;
  const response = (err as RequestError | undefined)?.response;
  return readMessage(response?.data) ?? fallback;
};

/** The HTTP status of a failed request, when there was one. */
export const apiErrorStatus = (err: unknown): number | undefined =>
  (err as RequestError | undefined)?.response?.status;
